import { skipToken, useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { useAuthStore } from './auth.store';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type { User } from '@/types/user';

// User entity lives in the TanStack Query cache (STATE_POLICY: server data
// → Query). Auth flows seed it via `profileService.applyProfileData`; this
// hook is a pure cache subscription — `skipToken` marks the query as
// intentionally unfetchable (the cache is populated by the auth pipeline /
// WS handler) and satisfies v5's dev-mode "No queryFn" check.
export const useUser = (): User | null => {
  const { data } = useQuery<User>({
    queryKey: cacheKeys.user,
    queryFn: skipToken,
    enabled: false,
    staleTime: Infinity,
  });
  return data ?? null;
};

// Atomic selectors for single values
export const useToken = () => useAuthStore((state) => state.token);
export const useIsAuthenticated = () =>
  useAuthStore((state) => state.isAuthenticated);
export const useIsInitialized = () =>
  useAuthStore((state) => state.isInitialized);

// Loading state selectors
export const useAuthLoading = () =>
  useAuthStore(
    (state) =>
      state.isLoggingIn ||
      state.isRegistering ||
      state.isLoggingOut ||
      state.isRefreshingUser ||
      state.isSendingPasswordReset ||
      state.isResettingPassword
  );
export const useLoginLoading = () => useAuthStore((state) => state.isLoggingIn);
export const useRegisterLoading = () =>
  useAuthStore((state) => state.isRegistering);
export const useLogoutLoading = () =>
  useAuthStore((state) => state.isLoggingOut);

// Error selectors - unified
export const useHasError = () =>
  useAuthStore(
    (state) =>
      !!(
        state.loginError ||
        state.registerError ||
        state.refreshUserError ||
        state.forgotPasswordError ||
        state.resetPasswordError
      )
  );
export const useAuthError = () =>
  useAuthStore(
    (state) =>
      state.loginError ||
      state.registerError ||
      state.refreshUserError ||
      state.forgotPasswordError ||
      state.resetPasswordError
  );
export const useLoginError = () => useAuthStore((state) => state.loginError);
export const useRegisterError = () =>
  useAuthStore((state) => state.registerError);

// Selector for user profile data
export const useUserProfile = () => {
  const user = useUser();
  return {
    id: user?.id || null,
    email: user?.email || null,
    isVerified: user?.email_verified_at !== null && user?.email_verified_at !== undefined,
  };
};

// Selector for auth state (primary selector for auth status)
export const useAuthState = () => {
  const user = useUser();
  const authFlags = useAuthStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      isInitialized: state.isInitialized,
      loading:
        state.isLoggingIn ||
        state.isRegistering ||
        state.isLoggingOut ||
        state.isRefreshingUser ||
        state.isSendingPasswordReset ||
        state.isResettingPassword,
      error:
        state.loginError ||
        state.registerError ||
        state.refreshUserError ||
        state.forgotPasswordError ||
        state.resetPasswordError,
    }))
  );
  return { user, ...authFlags };
};

// Selector for auth actions only
export const useAuthActions = () =>
  useAuthStore(
    useShallow((state) => ({
      login: state.login,
      register: state.register,
      logout: state.logout,
      refreshUser: state.refreshUser,
      clearError: state.clearErrors,
      forgotPassword: state.forgotPassword,
      resetPassword: state.resetPassword,
      initializeAuth: state.initializeAuth,
      getGoogleOAuthUrl: state.getGoogleOAuthUrl,
      sendVerificationCode: state.sendVerificationCode,
      verifyEmailCode: state.verifyEmailCode,
    }))
  );

// Selector for checking if user email is verified
export const useIsEmailVerified = (): boolean => {
  const user = useUser();
  return !!user && user.email_verified_at !== null && user.email_verified_at !== undefined;
};

// Selector for pending email verification (403 on profile fetch or unverified user)
export const usePendingEmailVerification = () =>
  useAuthStore((state) => state.pendingEmailVerification);
